import { useTranslation } from 'next-i18next'
import { looksLikeABot, looksLikeALocale } from '@captaincause/lib/api/path'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { ProjectPage } from '@captaincause/ui/ProjectPage'
import { getProject, getProjects } from 'api/projects'
import locales from 'i18n/locales'
import { NavAndFooterLayout } from 'layouts/NavAndFooter'
import { getBanner } from 'pages/api/banner'
import { PageHead } from 'pages/Head'
import { injectI18n } from 'utils/i18n'
import { Button } from 'components/Button'
import { CampaignStateBanner } from 'components/CampaignStateBanner'
import type { Locales, Project as ProjectType } from '@captaincause/types'
import type { NextPageWithLayout } from 'app'
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next'

interface PageProps {
  project: ProjectType
}

// TODO: Apply localizations when project give alternate slug

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const allProjects = await Promise.all(
      locales.SUPPORTED_LOCALES.map(locale =>
        getProjects({ locale: locale as Locales }).then(projects => ({
          projects,
          locale,
        })),
      ),
    )

    return {
      paths: [
        ...allProjects.map(({ projects, locale }) =>
          projects.map(project => ({
            params: { slug: project.slug },
            locale,
          })),
        ),
      ].flat(),
      fallback: 'blocking',
    }
  } catch (error: unknown) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
}

export const getStaticProps: GetStaticProps<PageProps> = async context => {
  if (!locales.SUPPORTED_LOCALES.includes(context.locale as string)) {
    return { notFound: true }
  }

  const i18n = await injectI18n(context.locale, [
    'campaign-banner.component',
    'footer.component',
    'metadata',
    'nav.component',
    'projects.page',
  ])

  const banner = await getBanner({ locale: context.locale as Locales })

  const id = context?.params?.slug as string

  if (looksLikeABot(id) || looksLikeALocale(id)) {
    return {
      notFound: true,
    }
  }

  try {
    const project = await getProject({ id, locale: context.locale as Locales })

    const enhancedLocalizations = project.localizations.map(localization => ({
      ...localization,
      slug: `/projects/${localization.slug ?? project.slug}`,
    }))

    return {
      props: {
        project,
        localizations: enhancedLocalizations,
        banner,
        ...i18n,
      },
      revalidate: 60,
    }
  } catch (error: unknown) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }
}

// ---

type InferredPageProps = InferGetStaticPropsType<typeof getStaticProps>
const Project: NextPageWithLayout<InferredPageProps> = ({
  project,
}: InferredPageProps) => {
  const { t } = useTranslation(['common', 'projects.page'])

  return (
    <>
      <PageHead
        head={{
          title: `${project.name}`,
          description: project?.goal || undefined,
        }}
        image={project?.image || undefined}
      />

      <CampaignStateBanner project={project} />
      <div className="mx-auto mt-4 hidden w-full max-w-7xl justify-start md:block md:px-10">
        <Button
          iconLeft="arrow-back"
          variant="naked"
          href="/projects"
          iconClassName="min-h-[16px] w-4 h-4 min-w-[16px]"
          className="pl-9 text-sm"
        >
          {t('common:back-to-projects')}
        </Button>
      </div>
      <ProjectPage
        project={project}
        Link={Button}
        locale={useNextLocale()}
        CallToAction={
          <Button variant="primary" href="/me/buy" full>
            {t('projects.page:project.dift.cta')}
          </Button>
        }
        className="pt-0 md:pt-4"
      />
    </>
  )
}

Project.Layout = NavAndFooterLayout
Project.pagePropsOnLayout = true

export default Project
